import { ESdChangeReason, ICommonSettings, IOTPSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import * as React from "react";
import { ReactNode } from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigOTPSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingOtp?: IOTPSetting;
}

export interface IConfigOTPSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingOtpId: string | null;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigOTPSettings extends React.PureComponent<IConfigOTPSettingsProps, IConfigOTPSettingsState> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigOTPSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingOtp: undefined
    };
  }

  componentDidMount() {
    this.loadOtpSettings();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigOTPSettingsProps) {
    if (prevProps.editingOtpId && !this.props.editingOtpId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingOtpId !== this.props.editingOtpId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadOtpSettings();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadOtpSettings();
    }
  }

  loadOtpSettings = () => {
    if (!this.props.editingOtpId) return;

    const otpSettings = _.cloneDeep(this.props.commonSettings?.otpSettings);
    const editingOtp = otpSettings?.find((setting) => setting.id === this.props.editingOtpId);
    if (editingOtp)
      this.setState({ editingOtp }, () => {
        this.formRef.current?.resetFields();
        this.formRef.current?.setFieldsValue(editingOtp);
      });
  };

  existingNameValidator = async (_rule, value) => {
    const otpSettings = this.props.commonSettings?.otpSettings;

    const isSettingAlreadyExist =
      otpSettings?.some((x) => x.name === value && x.id !== this.props.editingOtpId) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("OTP Setting Name is already exist.");
  };

  otpSecretKeyValidator = async (_rule, value) => {
    if (!value) throw new Error("Please enter an OTP Secret Key");

    if (!/^[a-zA-Z2-7]*$/.test(value))
      throw new Error("OTP Secret Key should only contain letters (A-Z, a-z), numbers (2-7)");
  };

  integerValidator = async (_rule, value) => {
    if (!value) throw new Error("This field is required.");
    if (!Number.isInteger(value)) throw new Error("Please enter an integer value.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    const obj: IOTPSetting = {
      id:
        this.props.editingOtpId && this.props.editingOtpId !== "" ? this.props.editingOtpId : Guid.create().toString(),
      name: data.name,
      secretKey: data.secretKey?.trim(),
      period: data.period,
      digit: data.digit,
      algorithm: data.algorithm
    };
    if (this.props.editingOtpId) {
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "otpSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "otpSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteOtp = () => {
    if (!this.props.editingOtpId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingOtpId || "",
      configKey: "otpSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`OTP setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  handleGenerateOTPSecret = () => {
    const secretKey = KMainFunctions.generateRandomString(32).replace(/[^A-Za-z2-7]/g, "");
    const fomrValues = { ...this.formRef.current?.getFieldsValue() };
    fomrValues.secretKey = secretKey;
    this.formRef.current?.setFieldsValue(fomrValues);
    this.formRef.current
      ?.validateFields()
      .then(() => {})
      .catch(() => {});
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="OTP Setting"
        secondHeader="OTP Setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingOtpId ? this.deleteOtp : undefined}
            deleteTestId="otpSettingDeleteButton"
            submitTestId="otpSettingCreateButton"
            submitLabel={this.props.editingOtpId ? "UPDATE" : "CREATE"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configOtpForm"
          />
        }
      >
        <Form
          id="configOtpForm"
          name="otpSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("appsettings_otpsettingpop_up_description")}>
            <div>
              <ConfigField label="Name" required tooltipContentKey="appsettings_otpsettingpop_up_name">
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Please enter a OTP Setting Name." },
                    { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "OTP Setting Name is invalid" },
                    { validator: this.existingNameValidator }
                  ]}
                >
                  <KuikaInput size="md" data-testid="otpSettingNameInput" autoFocus />
                </Form.Item>
              </ConfigField>

              <ConfigField label="Secret Key" required tooltipContentKey="appsettings_otpsettingpop_up_secretkey">
                <Form.Item name="secretKey" rules={[{ validator: this.otpSecretKeyValidator }]}>
                  <KuikaInput
                    size="md"
                    data-testid="otpSettingSecretKeyInput"
                    rightIcon={
                      <button type="button" className="kuika-input__action" onClick={this.handleGenerateOTPSecret}>
                        Generate
                      </button>
                    }
                  />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Period" required tooltipContentKey="appsettings_otpsettingpop_up_period">
                <Form.Item
                  name="period"
                  initialValue={30}
                  rules={[{ required: true, message: "Please select an OTP Period." }]}
                >
                  <KuikaSelectbox
                    size="md"
                    options={[
                      { value: 30, label: "30" },
                      { value: 60, label: "60" },
                      { value: 90, label: "90" }
                    ]}
                  />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Digit" required tooltipContentKey="appsettings_otpsettingpop_up_digit">
                <Form.Item
                  name="digit"
                  initialValue={6}
                  rules={[{ required: true, message: "Please select an OTP Digit." }]}
                >
                  <KuikaSelectbox
                    size="md"
                    options={[
                      { value: 6, label: "6" },
                      { value: 7, label: "7" },
                      { value: 8, label: "8" }
                    ]}
                  />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Algorithm" required tooltipContentKey="appsettings_otpsettingpop_up_algorithm">
                <Form.Item
                  name="algorithm"
                  initialValue="SHA1"
                  rules={[{ required: true, message: "Please select an Algorithm." }]}
                >
                  <KuikaSelectbox
                    size="md"
                    options={[
                      { value: "SHA1", label: "SHA1" },
                      { value: "SHA256", label: "SHA256" },
                      { value: "SHA512", label: "SHA512" }
                    ]}
                  />
                </Form.Item>
              </ConfigField>
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}
